import React, { useEffect, useState } from "react";
import { Link, Prompt, useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Steps,
  Switch,
  Tabs,
  Tag,
  Timeline,
  Typography
} from "antd";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  MoreOutlined,
  PlusOutlined,
  SaveOutlined,
  StopOutlined,
  UndoOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { centsToEuro, decimalInputProps, euroToCents, formatDay } from "../workReports/format";
import {
  addDeadline,
  archiveDocument,
  archivedUrl,
  deleteCase,
  deleteDeadline,
  documentUrl,
  getCase,
  getMeta,
  getSources,
  importCosts,
  updateCase,
  updateChecklist,
  updateDeadline
} from "./api";
import {
  STATUS_COLORS,
  TERMINAL,
  conditionLabels,
  documentLabels,
  fieldLabels,
  phaseLabels,
  providerLabels,
  statusLabels
} from "./labels";
import EstimateView, { WarningList } from "./EstimateView";
import StatusDialog from "./StatusDialog";
import CostLines from "./CostLines";
import ClauseCard from "./ClauseCard";
import "./funding.css";

// What the editor sends back with PUT (everything else changes through actions)
const EDITABLE = [
  "client",
  "project",
  "offer",
  "invoice",
  "object",
  "vatDeductible",
  "bonuses",
  "flags",
  "measures",
  "costLines",
  "advisorCents",
  "expert",
  "contract",
  "application",
  "approval",
  "execution",
  "proof",
  "payout",
  "certificate",
  "notes"
];

const idOf = value => (value && typeof value === "object" ? value._id : value) || null;

const formFrom = data => {
  const form = {};
  for (const key of EDITABLE) form[key] = JSON.parse(JSON.stringify(data[key] ?? null));
  for (const key of ["client", "project", "offer", "invoice"]) form[key] = idOf(data[key]);
  return form;
};

const STATE_TAGS = { ok: "success", fail: "error", missing: "warning" };

const DateInput = ({ value, onChange, disabled, month }) => (
  <DatePicker
    className="fu-full"
    picker={month ? "month" : undefined}
    format={month ? "MM.YYYY" : "DD.MM.YYYY"}
    disabled={disabled}
    value={value ? dayjs(month ? `${value}-01` : value) : null}
    onChange={next => onChange(next ? next.format(month ? "YYYY-MM" : "YYYY-MM-DD") : "")}
  />
);

const FundingCaseEditor = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const { id } = useParams();
  const history = useHistory();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sources, setSources] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [tab, setTab] = useState("overview");
  const [deadlineForm] = Form.useForm();

  const apply = data => {
    setView(data);
    setForm(formFrom(data.data));
    setDirty(false);
  };

  useEffect(() => {
    Promise.all([getMeta(), getCase(id)])
      .then(([metaRes, caseRes]) => {
        setMeta(metaRes.data);
        apply(caseRes.data);
      })
      .catch(generalErrorAlert);
  }, [id]);

  useEffect(() => {
    if (!view?.can.importDocuments) return;
    getSources(id)
      .then(res => setSources(res.data))
      .catch(generalErrorAlert);
  }, [id, view?.data.client?._id, view?.can.importDocuments]);

  const change = patch => {
    setForm(prev => ({ ...prev, ...patch }));
    setDirty(true);
  };
  const changeIn = (key, patch) => change({ [key]: { ...(form[key] || {}), ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateCase(id, form);
      apply(res.data);
      Success(t("Saved"));
      return true;
    } catch (error) {
      generalErrorAlert(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Actions save pending edits first so nothing typed is lost
  const run = async action => {
    if (dirty && !(await save())) return;
    try {
      const res = await action();
      if (res?.data?.data) apply(res.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  if (!view || !form || !meta) {
    return (
      <div className="funding-page">
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </div>
    );
  }

  const { data, program, can } = view;
  const labels = statusLabels(t);
  const canEdit = can.update;
  const pipeline = program.pipeline;
  const terminal = TERMINAL.includes(data.status);
  const currentIndex = pipeline.indexOf(terminal ? data.previousStatus : data.status);
  const next = !terminal ? pipeline[currentIndex + 1] : null;
  const fields = fieldLabels(t);
  const providers = providerLabels(t);
  const phases = phaseLabels(t);
  const docLabels = documentLabels(t);
  const choosableBonuses = (program.bonuses || []).filter(b => !b.requiresFlag);
  const hasIsfp = (program.bonuses || []).some(b => b.requiresFlag === "isfp");
  const errors = view.warnings.filter(w => w.level === "error");

  const moreItems = [
    ...(!terminal && canEdit
      ? pipeline
          .filter((s, i) => i !== currentIndex && i !== currentIndex + 1)
          .map(s => ({
            key: `to-${s}`,
            label: t("Set status: {{status}}", { status: labels[s] }),
            onClick: () => setStatusTarget(s)
          }))
      : []),
    ...(!terminal && canEdit
      ? [
          { type: "divider" },
          {
            key: "rejected",
            icon: <StopOutlined />,
            label: t("Funding rejected"),
            onClick: () => setStatusTarget("rejected")
          },
          {
            key: "cancelled",
            icon: <StopOutlined />,
            label: t("Cancel case"),
            onClick: () => setStatusTarget("cancelled")
          }
        ]
      : []),
    ...(terminal && canEdit && data.previousStatus
      ? [
          {
            key: "reopen",
            icon: <UndoOutlined />,
            label: t("Reopen"),
            onClick: () => setStatusTarget(data.previousStatus)
          }
        ]
      : []),
    ...(can.delete && ["advised", ...TERMINAL].includes(data.status)
      ? [
          {
            key: "delete",
            icon: <DeleteOutlined />,
            danger: true,
            label: t("Delete"),
            onClick: () =>
              Modal.confirm({
                rootClassName: "funding-page",
                title: t("Delete this funding case?"),
                okText: t("Delete"),
                okButtonProps: { danger: true },
                cancelText: t("Cancel"),
                onOk: () =>
                  deleteCase(id)
                    .then(() => history.push("/user/funding"))
                    .catch(generalErrorAlert)
              })
          }
        ]
      : [])
  ];

  // ---- Tabs ----
  const overview = (
    <Form layout="vertical" disabled={!canEdit}>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item label={t("Customer")}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              value={form.client || undefined}
              onChange={value => change({ client: value || null })}
              options={meta.clients.map(c => ({ value: c._id, label: c.name }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t("Project")}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              value={form.project || undefined}
              onChange={value => change({ project: value || null })}
              options={meta.projects.map(p => ({ value: p._id, label: p.name }))}
            />
          </Form.Item>
        </Col>
        {sources && (
          <>
            <Col xs={24} md={12}>
              <Form.Item label={t("Offer")}>
                <Select
                  allowClear
                  value={form.offer || undefined}
                  onChange={value => change({ offer: value || null })}
                  options={sources.offers.map(o => ({ value: o._id, label: o.label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("Final invoice")}>
                <Select
                  allowClear
                  value={form.invoice || undefined}
                  onChange={value => change({ invoice: value || null })}
                  options={sources.invoices.map(i => ({ value: i._id, label: i.label }))}
                />
              </Form.Item>
            </Col>
          </>
        )}
        <Col xs={24}>
          <Form.Item label={t("Address of the building")}>
            <Input
              value={form.object?.address}
              maxLength={300}
              onChange={e => changeIn("object", { address: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label={t("Dwelling units")}>
            <InputNumber
              className="fu-full"
              min={1}
              max={999}
              precision={0}
              value={form.object?.units}
              onChange={v => changeIn("object", { units: v || 1 })}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label={t("Year of construction")}>
            <InputNumber
              className="fu-full"
              min={1500}
              max={2100}
              precision={0}
              value={form.object?.buildingYear}
              onChange={v => changeIn("object", { buildingYear: v || null })}
            />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label={t("Owner-occupied")}>
            <Switch
              checked={form.object?.ownerOccupied !== false}
              onChange={v => changeIn("object", { ownerOccupied: v })}
            />
          </Form.Item>
        </Col>
        {program.kind === "grant" && (
          <Col xs={12} md={6}>
            <Form.Item label={t("Applicant deducts input VAT")}>
              <Switch
                checked={Boolean(form.vatDeductible)}
                onChange={v => change({ vatDeductible: v })}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      {(choosableBonuses.length > 0 || hasIsfp) && (
        <Form.Item
          label={t("Confirmed bonus conditions")}
          extra={t("Only tick what the customer has confirmed and can prove.")}
        >
          <Space direction="vertical">
            {choosableBonuses.map(b => (
              <Checkbox
                key={b.key}
                checked={(form.bonuses || []).includes(b.key)}
                onChange={e =>
                  change({
                    bonuses: e.target.checked
                      ? [...(form.bonuses || []), b.key]
                      : (form.bonuses || []).filter(k => k !== b.key)
                  })
                }
              >
                {b.label} ({b.flatCents ? `${b.flatCents / 100} €` : `${b.percent} %`})
              </Checkbox>
            ))}
            {hasIsfp && (
              <Checkbox
                checked={Boolean(form.flags?.isfp)}
                onChange={e => changeIn("flags", { isfp: e.target.checked })}
              >
                {t("Measure is part of an individual renovation roadmap (iSFP)")}
              </Checkbox>
            )}
          </Space>
        </Form.Item>
      )}

      {program.kind === "tax" && (
        <Form.Item label={t("Energy advisor costs (gross)")}>
          <InputNumber
            className="fu-full"
            min={0}
            precision={2}
            addonAfter="€"
            value={centsToEuro(form.advisorCents)}
            {...decimalInputProps(lng)}
            onChange={v => change({ advisorCents: euroToCents(v) })}
          />
        </Form.Item>
      )}

      {(program.expertRequired || form.expert?.name) && (
        <>
          <Typography.Title level={5}>{t("Energy efficiency expert")}</Typography.Title>
          <Row gutter={[16, 0]}>
            {[
              ["name", t("Name")],
              ["email", t("Email")],
              ["phone", t("Phone")],
              ["listNumber", t("Expert list number")]
            ].map(([key, label]) => (
              <Col xs={24} md={12} key={key}>
                <Form.Item label={label}>
                  <Input
                    value={form.expert?.[key]}
                    onChange={e => changeIn("expert", { [key]: e.target.value })}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>
        </>
      )}

      <Typography.Title level={5}>{t("Process data")}</Typography.Title>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={8}>
          <Form.Item label={fields["contract.signedAt"]}>
            <DateInput
              value={form.contract?.signedAt}
              onChange={v => changeIn("contract", { signedAt: v })}
              disabled={!canEdit}
            />
          </Form.Item>
        </Col>
        {program.conditionRequired && (
          <>
            <Col xs={24} md={8}>
              <Form.Item label={fields["contract.condition"]}>
                <Select
                  allowClear
                  value={form.contract?.condition || undefined}
                  onChange={v => changeIn("contract", { condition: v || "" })}
                  options={Object.entries(conditionLabels(t)).map(([value, label]) => ({
                    value,
                    label
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["contract.plannedDate"]}>
                <DateInput
                  month
                  value={form.contract?.plannedDate}
                  onChange={v => changeIn("contract", { plannedDate: v })}
                  disabled={!canEdit}
                />
              </Form.Item>
            </Col>
          </>
        )}
        {program.kind === "grant" && (
          <>
            <Col xs={24} md={8}>
              <Form.Item label={fields["application.submittedAt"]}>
                <DateInput
                  value={form.application?.submittedAt}
                  onChange={v => changeIn("application", { submittedAt: v })}
                  disabled={!canEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["application.number"]}>
                <Input
                  value={form.application?.number}
                  onChange={e => changeIn("application", { number: e.target.value })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["application.confirmationId"]}>
                <Input
                  value={form.application?.confirmationId}
                  onChange={e => changeIn("application", { confirmationId: e.target.value })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["approval.date"]}>
                <DateInput
                  value={form.approval?.date}
                  onChange={v => changeIn("approval", { date: v })}
                  disabled={!canEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["approval.amountCents"]}>
                <InputNumber
                  className="fu-full"
                  min={0}
                  precision={2}
                  addonAfter="€"
                  value={
                    form.approval?.amountCents == null
                      ? null
                      : centsToEuro(form.approval.amountCents)
                  }
                  {...decimalInputProps(lng)}
                  onChange={v =>
                    changeIn("approval", { amountCents: v == null ? null : euroToCents(v) })
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["approval.reference"]}>
                <Input
                  value={form.approval?.reference}
                  onChange={e => changeIn("approval", { reference: e.target.value })}
                />
              </Form.Item>
            </Col>
          </>
        )}
        <Col xs={24} md={8}>
          <Form.Item label={fields["execution.startedAt"]}>
            <DateInput
              value={form.execution?.startedAt}
              onChange={v => changeIn("execution", { startedAt: v })}
              disabled={!canEdit}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={fields["execution.completedAt"]}>
            <DateInput
              value={form.execution?.completedAt}
              onChange={v => changeIn("execution", { completedAt: v })}
              disabled={!canEdit}
            />
          </Form.Item>
        </Col>
        {program.kind === "grant" ? (
          <>
            <Col xs={24} md={8}>
              <Form.Item label={fields["proof.submittedAt"]}>
                <DateInput
                  value={form.proof?.submittedAt}
                  onChange={v => changeIn("proof", { submittedAt: v })}
                  disabled={!canEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["payout.date"]}>
                <DateInput
                  value={form.payout?.date}
                  onChange={v => changeIn("payout", { date: v })}
                  disabled={!canEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={fields["payout.amountCents"]}>
                <InputNumber
                  className="fu-full"
                  min={0}
                  precision={2}
                  addonAfter="€"
                  value={
                    form.payout?.amountCents == null ? null : centsToEuro(form.payout.amountCents)
                  }
                  {...decimalInputProps(lng)}
                  onChange={v =>
                    changeIn("payout", { amountCents: v == null ? null : euroToCents(v) })
                  }
                />
              </Form.Item>
            </Col>
          </>
        ) : (
          <Col xs={24} md={8}>
            <Form.Item label={fields["certificate.issuedAt"]}>
              <DateInput
                value={form.certificate?.issuedAt}
                onChange={v => changeIn("certificate", { issuedAt: v })}
                disabled={!canEdit}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Form.Item label={t("Notes")}>
        <Input.TextArea
          value={form.notes}
          autoSize={{ minRows: 2, maxRows: 8 }}
          maxLength={10000}
          onChange={e => change({ notes: e.target.value })}
        />
      </Form.Item>
    </Form>
  );

  const selectedMeasures = new Map((form.measures || []).map(m => [m.key, m]));
  const serverMeasures = new Map(view.measures.map(m => [m.key, m]));
  const setMeasure = (key, patch) =>
    change({ measures: (form.measures || []).map(m => (m.key === key ? { ...m, ...patch } : m)) });
  const measuresTab = (
    <div>
      <Checkbox.Group
        className="fu-checks mb-3"
        disabled={!canEdit}
        value={[...selectedMeasures.keys()]}
        onChange={keys =>
          change({
            measures: keys.map(key => selectedMeasures.get(key) || { key, values: {}, note: "" })
          })
        }
        options={program.measures.map(m => ({ value: m.key, label: m.label }))}
      />
      {(form.measures || []).map(entry => {
        const def = program.measures.find(m => m.key === entry.key);
        if (!def) return null;
        const states = new Map(
          (serverMeasures.get(entry.key)?.requirements || []).map(r => [r.key, r.state])
        );
        return (
          <Card key={entry.key} size="small" className="mb-3" title={def.label}>
            {!def.requirements?.length && (
              <Typography.Text type="secondary">
                {t("No technical values required.")}
              </Typography.Text>
            )}
            {(def.requirements || []).map(req => {
              const value = entry.values?.[req.key];
              const setValue = next =>
                setMeasure(entry.key, { values: { ...(entry.values || {}), [req.key]: next } });
              const state = states.get(req.key);
              return (
                <div key={req.key} className="fu-requirement">
                  <div className="fu-requirement-label">
                    {req.label}
                    {req.max !== undefined && (
                      <span className="fu-small">
                        {" "}
                        (≤ {req.max} {req.unit})
                      </span>
                    )}
                    {req.min !== undefined && (
                      <span className="fu-small">
                        {" "}
                        (≥ {req.min} {req.unit})
                      </span>
                    )}
                    {state && STATE_TAGS[state] && (
                      <Tag className="fu-state" color={STATE_TAGS[state]}>
                        {state === "ok" ? t("met") : state === "fail" ? t("not met") : t("open")}
                      </Tag>
                    )}
                  </div>
                  {req.type === "boolean" ? (
                    <Segmented
                      disabled={!canEdit}
                      value={value === true ? "yes" : value === false ? "no" : "open"}
                      onChange={v => setValue(v === "yes" ? true : v === "no" ? false : null)}
                      options={[
                        { value: "yes", label: t("Yes") },
                        { value: "no", label: t("No") },
                        { value: "open", label: "—" }
                      ]}
                    />
                  ) : req.type === "number" ? (
                    <InputNumber
                      className="fu-number"
                      disabled={!canEdit}
                      value={value ?? null}
                      addonAfter={req.unit || undefined}
                      {...decimalInputProps(lng)}
                      onChange={v => setValue(v)}
                    />
                  ) : (
                    <Input
                      disabled={!canEdit}
                      value={value || ""}
                      maxLength={200}
                      onChange={e => setValue(e.target.value)}
                    />
                  )}
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );

  const costsTab = (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={15}>
        <CostLines
          lines={form.costLines || []}
          measures={program.measures.filter(m => selectedMeasures.has(m.key))}
          costs={view.costs}
          sources={sources}
          canEdit={canEdit}
          canImport={can.importDocuments}
          onChange={costLines => change({ costLines })}
          onImport={(source, docId) => run(() => importCosts(id, { source, id: docId }))}
        />
      </Col>
      <Col xs={24} xl={9}>
        <Card size="small" title={t("Estimate")}>
          <EstimateView estimate={view.estimate} showWarnings={false} />
          {dirty && (
            <Alert type="info" className="mt-2" message={t("Save to update the estimate.")} />
          )}
        </Card>
      </Col>
    </Row>
  );

  const byPhase = view.checklist.reduce((groups, item) => {
    (groups[item.phase] = groups[item.phase] || []).push(item);
    return groups;
  }, {});
  const checklistTab = (
    <div>
      {Object.entries(byPhase).map(([phase, items]) => (
        <div key={phase} className="mb-3">
          <Typography.Title level={5}>{phases[phase] || phase}</Typography.Title>
          <List
            bordered
            dataSource={items}
            renderItem={item => (
              <List.Item
                actions={
                  item.document && item.document !== "clause"
                    ? [
                        <Button
                          key="pdf"
                          size="small"
                          icon={<FilePdfOutlined />}
                          onClick={() => window.open(documentUrl(id, item.document), "_blank")}
                        >
                          PDF
                        </Button>
                      ]
                    : item.document === "clause"
                      ? [
                          <Button key="clause" size="small" onClick={() => setTab("documents")}>
                            {t("Clause")}
                          </Button>
                        ]
                      : []
                }
              >
                <Space align="start">
                  <Checkbox
                    checked={item.done}
                    disabled={!canEdit || item.auto}
                    onChange={e =>
                      run(() => updateChecklist(id, item.key, { done: e.target.checked }))
                    }
                  />
                  <div>
                    <div className={item.done ? "fu-done" : ""}>{item.label}</div>
                    <Space size={4} wrap>
                      <Tag>{providers[item.provider] || item.provider}</Tag>
                      {item.auto && <Tag color="blue">{t("automatic")}</Tag>}
                    </Space>
                  </div>
                </Space>
              </List.Item>
            )}
          />
        </div>
      ))}
    </div>
  );

  const documentsTab = (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={program.conditionRequired ? 12 : 24}>
        <Card size="small" title={t("Documents (drafts)")}>
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message={t(
              "These documents are drafts. Compare them with the current official forms before handing them out."
            )}
          />
          <List
            dataSource={view.documentKinds}
            renderItem={kind => (
              <List.Item
                actions={[
                  <Button
                    key="pdf"
                    icon={<FilePdfOutlined />}
                    onClick={() => window.open(documentUrl(id, kind), "_blank")}
                  >
                    {t("Preview")}
                  </Button>,
                  canEdit && (
                    <Button
                      key="save"
                      icon={<SaveOutlined />}
                      onClick={() => run(() => archiveDocument(id, kind))}
                    >
                      {t("Save to files")}
                    </Button>
                  )
                ].filter(Boolean)}
              >
                {docLabels[kind]}
              </List.Item>
            )}
          />
          {data.documents?.length > 0 && (
            <>
              <Typography.Title level={5} className="mt-3">
                {t("Saved documents")}
              </Typography.Title>
              <List
                size="small"
                dataSource={[...data.documents].reverse()}
                renderItem={doc => (
                  <List.Item>
                    <a href={archivedUrl(id, doc._id)} target="_blank" rel="noreferrer">
                      {docLabels[doc.kind] || doc.kind}
                    </a>
                    <span className="fu-small">
                      {dayjs(doc.createdAt).format("DD.MM.YYYY HH:mm")}
                    </span>
                  </List.Item>
                )}
              />
            </>
          )}
        </Card>
      </Col>
      {program.conditionRequired && (
        <Col xs={24} lg={12}>
          <Card size="small" title={docLabels.clause}>
            <ClauseCard view={view} onUpdated={apply} />
          </Card>
        </Col>
      )}
    </Row>
  );

  const deadlinesTab = (
    <div>
      <List
        className="mb-3"
        bordered
        locale={{
          emptyText: (
            <Empty
              description={t(
                "No deadlines yet. Programme deadlines appear once the approval date is entered."
              )}
            />
          )
        }}
        dataSource={data.deadlines}
        renderItem={d => {
          const overdue = !d.doneAt && d.dueDate < dayjs().format("YYYY-MM-DD");
          return (
            <List.Item
              actions={
                d.source === "manual" && canEdit
                  ? [
                      <Button
                        key="done"
                        size="small"
                        icon={d.doneAt ? <UndoOutlined /> : <CheckOutlined />}
                        onClick={() => run(() => updateDeadline(id, d._id, { done: !d.doneAt }))}
                      >
                        {d.doneAt ? t("Reopen") : t("Done")}
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title={t("Delete this deadline?")}
                        onConfirm={() => run(() => deleteDeadline(id, d._id))}
                      >
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label={t("Delete")}
                        />
                      </Popconfirm>
                    ]
                  : []
              }
            >
              <List.Item.Meta
                title={
                  <Space wrap>
                    <span className={d.doneAt ? "fu-done" : ""}>{d.title}</span>
                    {d.doneAt ? (
                      <Tag color="success">{t("done")}</Tag>
                    ) : overdue ? (
                      <Tag color="error">{t("overdue")}</Tag>
                    ) : null}
                  </Space>
                }
                description={
                  <Space wrap size={4}>
                    <span>{formatDay(d.dueDate)}</span>
                    <Tag>{d.source === "rule" ? t("Programme") : t("Own")}</Tag>
                    {d.verify && <Tag color="warning">{t("period to verify")}</Tag>}
                    {d.internal && <Tag>{t("internal")}</Tag>}
                  </Space>
                }
              />
            </List.Item>
          );
        }}
      />
      {canEdit && (
        <Form
          form={deadlineForm}
          layout={isMobile ? "vertical" : "inline"}
          onFinish={values =>
            run(() =>
              addDeadline(id, { title: values.title, dueDate: values.dueDate.format("YYYY-MM-DD") })
            ).then(() => deadlineForm.resetFields())
          }
        >
          <Form.Item name="title" rules={[{ required: true, message: t("Please enter a title.") }]}>
            <Input placeholder={t("Own deadline")} maxLength={200} />
          </Form.Item>
          <Form.Item name="dueDate" rules={[{ required: true, message: t("Required") }]}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item>
            <Button htmlType="submit" icon={<PlusOutlined />}>
              {t("Add deadline")}
            </Button>
          </Form.Item>
        </Form>
      )}
      <Typography.Paragraph type="secondary" className="fu-small mt-2">
        {t("The company owner gets an email 30, 14, 7 and 1 days before and on the due date.")}
      </Typography.Paragraph>
    </div>
  );

  const historyTab = (
    <Timeline
      items={[...(data.history || [])].reverse().map((h, index) => ({
        key: index,
        children: (
          <div>
            <b>{dayjs(h.at).format("DD.MM.YYYY HH:mm")}</b>{" "}
            {h.action === "status"
              ? `${labels[h.from] || h.from} → ${labels[h.to] || h.to}`
              : h.action === "document"
                ? `${t("Document saved")}: ${docLabels[h.to] || h.to}`
                : h.action === "clause"
                  ? t("Clause added to the offer")
                  : h.action === "import"
                    ? t("Costs imported")
                    : h.action === "create"
                      ? t("Created")
                      : h.action === "checklist"
                        ? `${t("Checklist")}: ${h.to}`
                        : h.action}
            {h.note && h.action === "status" && <div className="fu-small">{h.note}</div>}
          </div>
        )
      }))}
    />
  );

  return (
    <div className="funding-page">
      <Prompt when={dirty && !saving} message={t("You have unsaved changes. Leave anyway?")} />

      <Card className="mb-3">
        <div className="fu-header">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label={t("Back")}
            onClick={() => history.push("/user/funding")}
          />
          <div className="fu-header-title">
            <Typography.Title level={4}>{data.number}</Typography.Title>
            <Tag color={STATUS_COLORS[data.status]}>{labels[data.status]}</Tag>
          </div>
          <Space wrap>
            {canEdit && (
              <Button icon={<SaveOutlined />} loading={saving} disabled={!dirty} onClick={save}>
                {t("Save")}
              </Button>
            )}
            {canEdit && next && (
              <Button
                type="primary"
                onClick={() =>
                  dirty ? save().then(ok => ok && setStatusTarget(next)) : setStatusTarget(next)
                }
              >
                {t("Next: {{status}}", { status: labels[next] })}
              </Button>
            )}
            {moreItems.length > 0 && (
              <Dropdown menu={{ items: moreItems }} trigger={["click"]}>
                <Button icon={<MoreOutlined />} aria-label={t("More")} />
              </Dropdown>
            )}
          </Space>
        </div>
        <div className="fu-program">
          {program.name}
          {program.frozen && (
            <Tag className="fu-state">{t("rules fixed with the application")}</Tag>
          )}
          {data.client && <span className="fu-small"> · {data.client.name}</span>}
          {data.project && (
            <span className="fu-small">
              {" · "}
              <Link to={`/user/projects/${data.project._id}`}>{data.project.name}</Link>
            </span>
          )}
        </div>
        <Steps
          className="fu-steps"
          size="small"
          direction={isMobile ? "vertical" : "horizontal"}
          current={currentIndex}
          status={terminal ? "error" : undefined}
          items={pipeline.map(s => ({ title: labels[s] }))}
        />
        {terminal && data.closedReason && (
          <Alert
            className="mt-2"
            type="warning"
            showIcon
            message={`${labels[data.status]}: ${data.closedReason}`}
          />
        )}
      </Card>

      {view.warnings.length > 0 && (
        <Card
          size="small"
          className="mb-3"
          title={
            errors.length
              ? t("{{n}} problems need attention", { n: errors.length })
              : t("Notes for this case")
          }
        >
          <WarningList warnings={view.warnings} />
        </Card>
      )}

      <Card>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "overview", label: t("Overview"), children: overview },
            { key: "measures", label: t("Measures"), children: measuresTab },
            { key: "costs", label: t("Costs and estimate"), children: costsTab },
            { key: "checklist", label: t("Checklist"), children: checklistTab },
            { key: "documents", label: t("Documents"), children: documentsTab },
            { key: "deadlines", label: t("Deadlines"), children: deadlinesTab },
            { key: "history", label: t("History"), children: historyTab }
          ]}
        />
      </Card>

      <StatusDialog
        open={Boolean(statusTarget)}
        view={view}
        target={statusTarget}
        onClose={() => setStatusTarget(null)}
        onDone={result => {
          apply(result);
          setStatusTarget(null);
        }}
      />
    </div>
  );
};

export default FundingCaseEditor;
